import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Filter, X, Users, ChevronDown } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leaders = () => {
  const navigate = useNavigate();
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    state: '',
    district: '',
    role: '',
    party: '',
    status: 'active'
  });
  
  // Available filter options
  const [roles, setRoles] = useState([]);
  const [states, setStates] = useState([]);
  const [parties, setParties] = useState([]);

  useEffect(() => {
    fetchLeaders();
    fetchRoles();
  }, []);

  useEffect(() => {
    // Debounced search
    const timer = setTimeout(() => {
      fetchLeaders();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, filters]);

  const fetchLeaders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.state) params.append('state', filters.state);
      if (filters.district) params.append('district', filters.district);
      if (filters.role) params.append('role', filters.role);
      if (filters.party) params.append('party', filters.party);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(`${API_URL}/api/leaders?${params.toString()}`);
      const sorted = response.data.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
      setLeaders(sorted);
      
      // Extract unique values for filters
      const uniqueStates = [...new Set(response.data.map(l => l.state).filter(Boolean))];
      const uniqueParties = [...new Set(response.data.map(l => l.party).filter(Boolean))];
      setStates(uniqueStates);
      setParties(uniqueParties);
    } catch (error) {
      console.error('Error fetching leaders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/leaders/roles`);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setRoles(['MP', 'MLA', 'Mayor', 'Councillor']);
    }
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      district: '',
      role: '',
      party: '',
      status: 'active'
    });
    setSearchTerm('');
  };

  const hasActiveFilters = filters.state || filters.district || filters.role || filters.party || filters.status !== 'active';

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Active</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Former</span>;
  };

  const getRoleBadge = (role) => {
    const colors = {
      'MP': 'bg-purple-100 text-purple-700',
      'MLA': 'bg-blue-100 text-blue-700',
      'Mayor': 'bg-orange-100 text-orange-700',
      'Councillor': 'bg-teal-100 text-teal-700',
    };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[role] || 'bg-gray-100 text-gray-700'}`}>
        {role}
      </span>
    );
  };

  if (loading && leaders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-8 w-8 text-blue-600" />
            <h1 className="text-4xl font-bold text-gray-900">Leaders</h1>
          </div>
          <p className="text-base text-gray-600 mb-6">Rate and review your elected representatives</p>

          {/* Search and Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                data-testid="search-leaders-input"
                type="text"
                placeholder="Search by name, constituency, or party..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {Object.values(filters).filter(v => v && v !== 'active').length}
                </span>
              )}
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-gray-500 hover:text-gray-700"
                data-testid="clear-filters-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <Select value={filters.role || "all"} onValueChange={(v) => setFilters({...filters, role: v === "all" ? "" : v})}>
                  <SelectTrigger data-testid="filter-role">
                    <SelectValue placeholder="All Roles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {roles.map(role => (
                      <SelectItem key={role} value={role}>{role}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <Select value={filters.state || "all"} onValueChange={(v) => setFilters({...filters, state: v === "all" ? "" : v})}>
                  <SelectTrigger data-testid="filter-state">
                    <SelectValue placeholder="All States" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px] overflow-y-auto">
                    <SelectItem value="all">All States</SelectItem>
                    {states.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Party</label>
                <Select value={filters.party || "all"} onValueChange={(v) => setFilters({...filters, party: v === "all" ? "" : v})}>
                  <SelectTrigger data-testid="filter-party">
                    <SelectValue placeholder="All Parties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Parties</SelectItem>
                    {parties.map(party => (
                      <SelectItem key={party} value={party}>{party}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                  <SelectTrigger data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="former">Former</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <Input
                  data-testid="filter-district"
                  type="text"
                  placeholder="Enter district..."
                  value={filters.district}
                  onChange={(e) => setFilters({...filters, district: e.target.value})}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-gray-500">
          Showing {leaders.length} leader{leaders.length !== 1 ? 's' : ''}
        </div>

        {/* Leaders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {leaders.map((leader) => (
            <div
              key={leader.leader_id || leader.politician_id}
              data-testid={`leader-card-${leader.leader_id || leader.politician_id}`}
              onClick={() => navigate(`/leaders/${leader.leader_id || leader.politician_id}`)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="relative mb-4">
                <img
                  src={leader.photo_url || leader.profile_image || '/logo.png'}
                  alt={leader.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                  onError={(e) => { e.target.src = '/logo.png'; }}
                />
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2">
                  {getStatusBadge(leader.status || 'active')}
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{leader.name}</h3>
              
              <div className="flex flex-wrap gap-1 justify-center mb-2">
                {getRoleBadge(leader.role || 'MLA')}
                <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  {leader.party}
                </span>
              </div>
              
              <p className="text-xs text-gray-500 mb-3">
                {leader.constituency}, {leader.state}
              </p>
              
              {leader.term_start && (
                <p className="text-xs text-gray-400 mb-2">
                  Since {leader.term_start}
                </p>
              )}
              
              <div className="flex items-center gap-1 mb-2">
                <span className="text-2xl font-bold text-blue-600">
                  {(leader.avg_rating || 0).toFixed(1)}
                </span>
                <span className="text-yellow-400 text-xl">★</span>
              </div>
              
              <div className="flex gap-4 text-xs text-gray-500">
                <span>{leader.rating_count || 0} ratings</span>
                <span>{leader.issue_count || 0} issues</span>
              </div>
            </div>
          ))}
        </div>

        {leaders.length === 0 && (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No leaders found</p>
            <p className="text-gray-400 text-sm mt-1">Try adjusting your filters or search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaders;
